import { useCallback, useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { EnhancedMovieCard } from "./EnhancedMovieCard";
import { Sparkles, RefreshCw } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { InlineError } from "@/components/ui/inline-error";


interface SimilarMoviesProps {
  movieId: number;
  mediaType: 'movie' | 'tv';
  userId?: string;
  isPremium?: boolean;
}

interface Movie {
  id: number;
  title: string;
  year: number | null;
  overview: string;
  poster: string | null;
  rating: number;
  media_type: 'movie' | 'tv';
}

export const SimilarMovies = ({ 
  movieId, 
  mediaType, 
  userId, 
  isPremium = false,
}: SimilarMoviesProps) => {
  const [similarMovies, setSimilarMovies] = useState<Movie[]>([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const { toast } = useToast();

  const fetchSimilarMovies = useCallback(async () => {
    if (!userId) return; // Require authentication

    setLoading(true);
    setFailed(false);
    try {
      const { data, error } = await supabase.functions.invoke('tmdb-similar', {
        body: { tmdbId: movieId, mediaType }
      });

      if (error) {
        // Handle auth errors gracefully
        if (error.message?.includes('Unauthorized') || error.message?.includes('401')) {
          console.log('Auth required for similar movies');
          return;
        }
        throw error;
      }
      setSimilarMovies(data.results || []);
    } catch (error) {
      console.error('Error fetching similar movies:', error);
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [movieId, mediaType, userId]);

  useEffect(() => {
    if (movieId && userId) {
      fetchSimilarMovies();
    }
  }, [fetchSimilarMovies, movieId, userId]);

  // Don't render if not authenticated
  if (!userId) {
    return null;
  }

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Sparkles className="h-5 w-5 text-primary" />
            Similar {mediaType === 'tv' ? 'Shows' : 'Movies'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ScrollArea className="w-full">
            <div className="flex gap-4 pb-4">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="w-40 flex-shrink-0 aspect-[2/3] bg-muted animate-pulse rounded-lg" />
              ))}
            </div>
            <ScrollBar orientation="horizontal" />
          </ScrollArea>
        </CardContent>
      </Card>
    );
  }

  if (failed) {
    return (
      <Card className="border-primary/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Sparkles className="h-5 w-5 text-primary" />
            You Might Also Like
          </CardTitle>
        </CardHeader>
        <CardContent>
          <InlineError
            message="We couldn't load titles like this one right now."
            onRetry={fetchSimilarMovies}
          />
        </CardContent>
      </Card>
    );
  }

  if (similarMovies.length === 0) return null;


  return (
    <Card className="border-primary/20">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Sparkles className="h-5 w-5 text-primary" />
            You Might Also Like
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={fetchSimilarMovies}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <ScrollArea className="w-full">
          <div className="flex gap-4 pb-4">
            {similarMovies.map((movie) => (
              <div key={movie.id} className="w-40 flex-shrink-0">
                <EnhancedMovieCard
                  id={movie.id}
                  title={movie.title}
                  year={movie.year}
                  overview={movie.overview}
                  poster={movie.poster}
                  rating={movie.rating}
                  media_type={movie.media_type}
                  isPremium={isPremium}
                  userId={userId}
                />
              </div>
            ))}
          </div>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>
      </CardContent>
    </Card>
  );
};